import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowRight, Newspaper, Hash, Share2, InboxIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import SeoHead from '@/components/Seo';

interface NewsPost {
    id: number;
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    author: string;
    category: string;
    published_at: string;
    image: string;
    is_featured: boolean;
}

const NewsPage = () => {
    const [news, setNews] = useState<NewsPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('All');
    const [activeYear, setActiveYear] = useState('All');

    useEffect(() => {
        axios.get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/news')
            .then(res => {
                // Assuming res.data.news is the array of news objects
                setNews(res.data.news);
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, []);

    // 0. Generate unique categories list
    const categories = ['All', ...Array.from(new Set(news.map(post => post.category)))];

    const years = ['All', ...Array.from(new Set(news.map(post =>
        new Date(post.published_at).getFullYear().toString()
    )))].sort((a, b) => b.localeCompare(a)); // Sort years descending (newest first)

    // 1. Find the first item where is_featured is 1 (or truthy)
    const featuredPost = news.find(post => Number(post.is_featured) === 1);

    // 2. Get all other posts that are NOT that specific featured post
    const regularPosts = news.filter(post => post.id !== featuredPost?.id);

    const filteredNews = news.filter(post => {
        const postYear = new Date(post.published_at).getFullYear().toString();
        const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
        const matchesYear = activeYear === 'All' || postYear === activeYear;
        const isNotFeatured = post.id !== featuredPost?.id;

        return matchesCategory && matchesYear && isNotFeatured;
    });

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    const handleShare = async (e: React.MouseEvent, post: NewsPost) => {
        e.preventDefault(); // Stop the Link from opening
        e.stopPropagation(); // Stop the event from bubbling up

        const shareData = {
            title: post.title,
            text: post.excerpt,
            url: `${window.location.origin}/news/${post.slug}`,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                // Fallback: Copy to clipboard if Web Share API isn't available
                await navigator.clipboard.writeText(shareData.url);
                alert('Link copied to clipboard!');
            }
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    return (
        <div className="bg-[#fcfaf7] min-h-screen pb-24">
            <SeoHead
                title="Diocesan News"
                description="Latest updates, announcements, and stories from the Diocese of Ibadan South."
            />

            {/* Header Section */}
            <header className="bg-slate-900 pt-24 pb-40 px-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:40px_40px]" />
                </div>

                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-500 text-[10px] font-bold uppercase tracking-widest">
                            <Newspaper size={14} /> The Diocesan Journal
                        </span>
                        <h1 className="text-5xl md:text-7xl font-serif font-bold text-white leading-tight">
                            News & <span className="text-amber-500 italic">Updates</span>
                        </h1>
                        <p className="text-slate-400 font-serif italic text-lg max-w-2xl mx-auto">
                            Chronicles of faith, growth, and community impact across the Diocese.
                        </p>
                    </motion.div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 -mt-24 relative z-20">
                {news.length > 0 && (
                    <>
                        {/* Featured Post - Only show if we are on 'All' or if it matches the category */}
                        {featuredPost && activeCategory === 'All' && (
                            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-20">
                                {/* ... existing featured post JSX ... */}
                            </motion.div>
                        )}

                        <div className="bg-white p-4 rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 flex flex-col gap-3">
                            {/* Categories */}
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[10px] font-bold uppercase text-slate-400 mr-2">Category:</span>
                                {categories.map((cat) => (
                                    <button
                                        key={cat}
                                        onClick={() => setActiveCategory(cat)}
                                        className={`px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${activeCategory === cat
                                            ? 'bg-amber-500 text-slate-900'
                                            : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                                            }`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>

                            {/* Years */}
                            <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-slate-50">
                                <span className="text-[10px] font-bold uppercase text-slate-400 mr-2">Archive Year:</span>
                                {years.map((yr) => (
                                    <button
                                        key={yr}
                                        onClick={() => setActiveYear(yr)}
                                        className={`px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${activeYear === yr
                                            ? 'bg-slate-900 text-white'
                                            : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                                            }`}
                                    >
                                        {yr}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </>
                )}
                {!loading && news.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-white rounded-[3rem] p-20 text-center shadow-xl shadow-slate-200/50 border border-slate-100"
                    >
                        <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 text-amber-500">
                            <InboxIcon size={40} />
                        </div>
                        <h2 className="text-3xl font-serif font-bold text-slate-900 mb-4">No Articles Found</h2>
                        <p className="text-slate-500 font-serif italic max-w-md mx-auto mb-8">
                            The journal is currently quiet. Please check back later for the latest updates from the Diocese.
                        </p>
                        <Link to="/" className="inline-flex items-center gap-2 text-amber-600 font-bold uppercase tracking-widest text-[10px] hover:gap-3 transition-all">
                            Return to Home <ArrowRight size={14} />
                        </Link>
                    </motion.div>
                ) : (
                    <>
                        {/* Featured Post */}
                        {featuredPost && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="mb-20"
                            >
                                <Link to={`/news/${featuredPost.slug}`} className="group">
                                    {/* ... featured post content ... */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 bg-white rounded-[3rem] overflow-hidden shadow-2xl shadow-slate-200/50">
                                        <div className="relative h-80 lg:h-full overflow-hidden">
                                            <img
                                                src={featuredPost.image}
                                                alt={featuredPost.title}
                                                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                                            />
                                            <div className="absolute top-6 left-6 px-4 py-2 bg-white/90 backdrop-blur-md rounded-full text-[10px] font-bold uppercase tracking-widest text-amber-600">
                                                Featured Story
                                            </div>
                                        </div>
                                        <div className="p-8 lg:p-16 flex flex-col justify-center">
                                            <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-6">
                                                <span className="flex items-center gap-1.5"><Calendar size={12} className="text-amber-500" /> {new Date(featuredPost.published_at).toLocaleDateString()}</span>
                                                <span className="flex items-center gap-1.5"><Hash size={12} className="text-amber-500" /> {featuredPost.category}</span>
                                            </div>
                                            <h2 className="text-3xl lg:text-5xl font-serif font-bold text-slate-900 group-hover:text-amber-600 transition-colors mb-6 leading-tight">
                                                {featuredPost.title}
                                            </h2>
                                            <p className="text-slate-500 text-lg leading-relaxed font-serif italic line-clamp-3 mb-8">
                                                {featuredPost.excerpt}
                                            </p>
                                            <div className="flex items-center gap-4 pt-8 border-t border-slate-100">
                                                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                                                    <User size={20} />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-bold uppercase text-slate-900 tracking-tighter">{featuredPost.author}</p>
                                                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">Diocesan Press</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        )}

                        {/* Regular News Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-4">
                            {filteredNews.map((post, index) => (
                                <motion.div
                                    key={post.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    {/* ... rest of the regular post card code ... */}
                                    <Link to={`/news/${post.slug}`} className="group block h-full">
                                        <Card className="border-none bg-transparent shadow-none h-full flex flex-col">
                                            <CardContent className="p-0 flex flex-col h-full">
                                                <div className="relative aspect-[16/10] rounded-[2rem] overflow-hidden shadow-xl shadow-slate-200/50 mb-8">
                                                    <img
                                                        src={post.image}
                                                        alt={post.title}
                                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                    />
                                                    <div className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-md rounded-xl text-[10px] font-bold uppercase tracking-widest text-slate-900">
                                                        {post.category}
                                                    </div>
                                                </div>
                                                <div className="px-2 space-y-4 flex-grow">
                                                    <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                                        <Calendar size={12} className="text-amber-500" />
                                                        {new Date(post.published_at).toLocaleDateString()}
                                                    </div>
                                                    <h3 className="text-2xl font-serif font-bold text-slate-900 group-hover:text-amber-600 transition-colors leading-snug">
                                                        {post.title}
                                                    </h3>
                                                    <p className="text-slate-500 text-sm leading-relaxed font-serif italic line-clamp-2">
                                                        {post.excerpt}
                                                    </p>
                                                </div>
                                                <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between group">
                                                    <span className="text-[10px] font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                                        Read Article <ArrowRight size={14} className="text-amber-500 group-hover:translate-x-1 transition-transform" />
                                                    </span>
                                                    <button
                                                        onClick={(e) => handleShare(e, post)} // Added onClick
                                                        className="p-2 text-slate-300 hover:text-amber-500 transition-colors relative z-30"
                                                    >
                                                        <Share2 size={16} />
                                                    </button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </>
                )}
                {/* --- EMPTY STATE END --- */}
                {filteredNews.length === 0 && (
                    <div className="text-center py-20">
                        <p className="text-slate-400 font-serif italic">No articles found matching these filters.</p>
                        <button
                            onClick={() => { setActiveCategory('All'); setActiveYear('All'); }}
                            className="mt-4 text-amber-600 text-xs font-bold uppercase tracking-widest"
                        >
                            Reset Filters
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
};

export default NewsPage;